import React, { useRef, useState } from 'react'
import { Divider, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery } from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import ArchiveIcon from '@mui/icons-material/Archive';
import EditIcon from '@mui/icons-material/Edit';

import globalStyles from "../../../assets/styles/global.module.scss";
import style from "../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss";
import dashboardStyles from '../../../assets/styles/componentStyles/callAnalysisStyles/callAnalysisDashboard.module.scss';

import MemoizedCustomVariableTableRow from './memoizedCustomVariableTableRow';
import GlobalButton from '../../utility/globalButton/button';
import GlobalSearch from '../../utility/GlobalSearch/GlobalSearch';
import { useFlowBuilderStore } from '../../../store/flowBuilderStore';
import { useVirtualizer } from '@tanstack/react-virtual';
import { tertiaryColor } from '../../../utils/colors';
import { customVariableDataInterface, globalMenuItemData } from '../../../models/axiosModel';

const CustomVariableTable = () => {

    const customVariableList = useFlowBuilderStore((state) => state.customVariableList) 
    const filteredCustomVariableList = useFlowBuilderStore((state) => state.filteredCustomVariableList)
    const setFilteredCustomVariableList = useFlowBuilderStore((state) => state.setFilteredCustomVariableList)
    const setFlagDataCollectionPopup = useFlowBuilderStore((state) => state.setFlagDataCollectionPopup)
    const setFlagUpdateFieldData = useFlowBuilderStore((state) => state.setFlagUpdateFieldData)
    const setExistingCustomVariablePayload = useFlowBuilderStore((state) => state.setExistingCustomVariablePayload)
    const existingCustomVariablePayload = useFlowBuilderStore((state) => state.existingCustomVariablePayload)
    const setFlagDeleteData = useFlowBuilderStore((state) => state.setFlagDeleteData)
    
    
    
    const tableContainerRef = useRef<HTMLDivElement>(null);
    
    const innerHeight = (window.innerHeight - 300);
    const [anchorEl, setAnchorEl] = useState<null | SVGSVGElement>(null);
    const [currentRowIndex, setCurrentRowIndex] = useState<string | null>(null);

    const breakPoints = {
        smallScreen: useMediaQuery<boolean>('(max-width : 600px) and (min-width : 0px)'),
    }

    const handleMenuOpen = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, rowIndex: string) => {
        setCurrentRowIndex(rowIndex);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setCurrentRowIndex(null);
        setAnchorEl(null);
    };

    const menuItem: globalMenuItemData[] = [
        {
            type: "item",
            label: "Edit",
            icon: <EditIcon className={globalStyles.menuIcon16px} />,
            onClick: (variable: customVariableDataInterface) => {
                setFlagDataCollectionPopup(true);
                setFlagUpdateFieldData(true);
                setExistingCustomVariablePayload({
                    ...existingCustomVariablePayload,
                    chatbot_custom_variable_config_id: variable.chatbot_custom_variable_configuration_id,
                    chatbot_custom_variable_name: variable.chatbot_custom_variable_name,
                    chatbot_custom_variable_fallback_value: variable.chatbot_custom_variable_fallback_value
                });
            }
        },
        {
            type: "item",
            label: "Archive",
            icon: <ArchiveIcon className={globalStyles.menuIcon16px} />,
            onClick: (variable: customVariableDataInterface) => {
                setExistingCustomVariablePayload({
                    ...existingCustomVariablePayload,
                    chatbot_custom_variable_config_id: variable.chatbot_custom_variable_configuration_id,
                    chatbot_custom_variable_name: variable.chatbot_custom_variable_name,
                    chatbot_custom_variable_fallback_value: variable.chatbot_custom_variable_fallback_value
                });
                setFlagDeleteData(true);
            }
        }
    ]

    const virtualizer = useVirtualizer({
        count: filteredCustomVariableList?.custom_variable_data?.length ?? 0,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => 48,
        overscan: 10,
        measureElement:
            typeof window !== 'undefined' && !navigator.userAgent.includes('Firefox')
                ? el => el?.getBoundingClientRect().height
                : undefined,
    });

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e?.target?.value?.toLowerCase();
        if (customVariableList) {
            const filter_data = customVariableList?.custom_variable_data?.filter((list: customVariableDataInterface) => {
                return (list?.chatbot_custom_variable_name?.toLowerCase().includes(query) || list?.chatbot_custom_variable_fallback_value?.toLowerCase().includes(query))
            })
            const updatedFilteredVariableList = {
                ...customVariableList, 
                custom_variable_data: filter_data,
            }
            setFilteredCustomVariableList(updatedFilteredVariableList);

        }
    }

    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <Grid container className={`${globalStyles.flexColumn} ${globalStyles.justifyCenter}`}>
                        <Grid item className={globalStyles.textCenter}>
                            <Typography className={style.popupTitleTypography}>Custom Variables</Typography>
                        </Grid>

                        <Divider className={style.popupTitleDivider} />

                        <Grid item className={globalStyles.fullwidth}>
                            <Grid container className={style.popupBodyGridContainer} gap={2}>   
                                <Grid item xs={12}>
                                    <Grid container justifyContent={breakPoints.smallScreen ? "start" : "end"}>
                                        <Grid item className={`${globalStyles.globalSearchbarSubItem}`}>
                                            <GlobalSearch
                                                fullWidth
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { handleSearch(e) }}
                                                size="small"
                                                placeholder="Search"
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12} className={globalStyles.fullwidth}>
                                    <TableContainer className={`${style.contactTableContainer} ${globalStyles.overflowAuto} ${globalStyles.relativePosition}`} ref={tableContainerRef} sx={{ maxHeight: `${innerHeight}px !important` }}>
                                        <Table stickyHeader>
                                            <TableHead sx={{ position: "sticky", top: "0", zIndex: "2" }}>
                                                <TableRow sx={{ display: "flex", width: "100% !important" }}> 
                                                    <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.fieldCellWidth}`}>
                                                        Variable Name
                                                    </TableCell>
                                                    <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.fieldCellWidth}`}>
                                                        Fallback Value
                                                    </TableCell> 
                                                    <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.fieldCellWidth}`}>
                                                        Archived
                                                    </TableCell> 
                                                    <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.lastColumnSticky} ${style.checkBoxCellWidth}`} sx={{ background: `${tertiaryColor} !important` }}></TableCell>
                                                </TableRow>
                                            </TableHead>

                                            <TableBody
                                                sx={{
                                                    display: virtualizer.getVirtualItems().length == 0 ? 'contents' : 'block',
                                                    height: virtualizer.getVirtualItems().length == 0 ? 'auto' : `${virtualizer.getTotalSize()}px`,
                                                    position: virtualizer.getVirtualItems().length == 0 ? 'static' : 'relative',
                                                }}
                                            >
                                                {virtualizer.getVirtualItems().length > 0 ?
                                                    virtualizer.getVirtualItems().map(virtualRow => {
                                                        const data = filteredCustomVariableList?.custom_variable_data?.[virtualRow.index];
                                                        if (!data) return null;

                                                        return (
                                                            <TableRow
                                                                key={data.chatbot_custom_variable_configuration_id}
                                                                data-index={virtualRow.index}
                                                                ref={node => virtualizer.measureElement(node)}
                                                                style={{
                                                                    position: 'absolute',
                                                                    transform: `translateY(${virtualRow.start}px)`,
                                                                    display: 'flex',
                                                                    width: '100%',
                                                                }}
                                                                className={globalStyles.tableRowGlobalStyle}
                                                            > 
                                                                <MemoizedCustomVariableTableRow
                                                                    data={data} 
                                                                    index={virtualRow?.index} 
                                                                    anchorEl={anchorEl}
                                                                    currentRowIndex={currentRowIndex}
                                                                    menuItem={menuItem}
                                                                    handleMenuOpen={handleMenuOpen}
                                                                    handleMenuClose={handleMenuClose}  
                                                                    isMenuOpen={data.chatbot_custom_variable_configuration_id == currentRowIndex}  
                                                                />
                                                            </TableRow>
                                                        );
                                                    })
                                                    :
                                                    <TableRow className={`${dashboardStyles.cellRow}`}>
                                                        <TableCell colSpan={12} className={globalStyles.textCenter}>
                                                            No Data Available
                                                        </TableCell>
                                                    </TableRow>
                                                }
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid> 
                                <Grid item xs={12}>
                                    <GlobalButton
                                        buttonText="New Variable"
                                        className='primaryButtonStyle'
                                        startIcon={<AddIcon />}
                                        sx={{ width: "160px" }}
                                        onClick={() => setFlagDataCollectionPopup(true)}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}

export default CustomVariableTable
